import React, { useCallback, useMemo } from 'react';

import { Select } from 'antd';

import { getIn } from 'formik';
import type { FieldProps } from 'formik';
import { Hint } from 'v2/components/Shared/Hint';

interface ISelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface IProps extends FieldProps {
  label?: string;
  labelItens?: string;
  multiple?: boolean;
  native?: boolean;
  options: ISelectOption[];
  loading?: boolean;
  emptyOption?: string;
  onChange?: (e: React.ChangeEvent<{ name?: string; value: unknown }>) => void;
  helperText?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
}

const MultipleValues = ({ options = [], field, label, loading, placeholder = 'Selecione', form, disabled }: IProps) => {
  const hasNoOptions = !options?.length;

  const handleChange = (values: any[]) => {
    form.setFieldValue(field.name, values);
  };

  const placeholderText = loading ? 'Carregando...' : hasNoOptions ? `Não há ${label?.toLowerCase()}` : placeholder;

  return (
    <Select
      mode='multiple'
      className='w-full'
      value={field.value || []}
      onChange={handleChange}
      placeholder={placeholderText}
      disabled={disabled || hasNoOptions}
      options={options?.map(op => ({
        label: op.label,
        value: op.value,
        disabled: op.disabled
      }))}
    />
  );
};

const SingleValue = ({
  options = [],
  field,
  label,
  loading,
  emptyOption,
  placeholder = 'Selecione uma opção',
  form,
  disabled,
  ...props
}: IProps) => {
  const hasNoOptions = !options?.length;

  const handleChange = useCallback(
    (value: any) => {
      const syntheticEvent = { target: { name: field.name, value } } as any;
      props?.onChange?.(syntheticEvent);
      form.setFieldValue(field.name, value);
    },
    [field, form, props]
  );

  const placeholderText = loading ? 'Carregando...' : hasNoOptions ? `Não há ${label?.toLowerCase()}` : placeholder;

  const selectOptions = useMemo(() => {
    const opts =
      options?.map(op => ({
        label: op.label,
        value: op.value,
        disabled: op.disabled
      })) || [];

    if (emptyOption) {
      opts.unshift({ label: emptyOption, value: '' as any, disabled: false });
    }

    return opts;
  }, [options, emptyOption]);

  return (
    <Select
      className='w-full'
      value={field.value === '' || field.value == null ? undefined : field.value}
      onChange={handleChange}
      placeholder={placeholderText}
      disabled={disabled}
      options={selectOptions}
    />
  );
};

export const SelectFormField = ({ field, form, label, helperText, ...props }: IProps) => {
  const errorText = getIn(form.errors, field.name);

  return (
    <Hint inputName={field.name}>
      <div className='w-full'>
        {label && <label className='mb-1 block text-sm'>{label}</label>}
        {props.multiple ? (
          <MultipleValues field={field} form={form} label={label} {...props} />
        ) : (
          <SingleValue field={field} form={form} label={label} {...props} />
        )}
        {errorText && <p className='mt-1 text-xs text-red-500'>{errorText}</p>}
        {!errorText && helperText && <p className='mt-1 text-xs text-gray-500'>{helperText}</p>}
      </div>
    </Hint>
  );
};
